import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControlLabel,
  Checkbox,
  Box,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Collapse,
  CircularProgress
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Assignment as AssignmentIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import toastUtils from "../../utils/toastUtils";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  settingContainer: {
    padding: '20px',
    border: '1px solid',
    borderColor: theme.palette.type === 'dark' ? '#444' : '#e0e0e0',
    borderRadius: '8px',
    backgroundColor: theme.palette.type === 'dark' ? '#252525' : '#ffffff',
    transition: 'all 0.2s',
    marginBottom: '16px'
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: '8px'
  },
  sectionIcon: {
    marginRight: '8px',
    color: theme.palette.primary.main,
  },
  addButton: {
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 650,
  },
  actionCell: {
    width: 120,
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  statusChip: {
    fontSize: '0.75rem',
    height: '20px'
  }
}));

const FinalizacaoAtendimento = ({ settings, onSettingsChange }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [motivos, setMotivos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editingMotivo, setEditingMotivo] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });
  const [informarValorVenda, setInformarValorVenda] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    general: true,
    motivos: true
  });

  useEffect(() => {
    if (settings) {
      setInformarValorVenda(settings.informarValorVenda || false);
    }
    fetchMotivos();
  }, [settings]);

  const fetchMotivos = useCallback(async () => {
    try {
      const { data } = await api.get("/ticketFinalizationReasons");
      setMotivos(data);
    } catch (err) {
      console.error("Erro ao buscar motivos:", err);
      toastUtils.error(i18n.t("finalizationSettings.errors.title"), i18n.t("finalizationSettings.errors.fetchFailed"));
    }
  }, []);

  const handleOpenModal = useCallback((motivo = null) => {
    if (motivo) {
      setEditingMotivo(motivo);
      setFormData({
        name: motivo.name,
        description: motivo.description || "",
      });
    } else {
      setEditingMotivo(null);
      setFormData({
        name: "",
        description: "",
      });
    }
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setEditingMotivo(null);
    setFormData({
      name: "",
      description: "",
    });
  }, []);

  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!formData.name.trim()) {
      toastUtils.error(i18n.t("finalizationSettings.errors.title"), i18n.t("finalizationSettings.errors.nameRequired"));
      return;
    }

    setLoading(true);
    try {
      if (editingMotivo) {
        await api.put(
          `/ticketFinalizationReasons/${editingMotivo.id}`,
          formData
        );
        toastUtils.success(i18n.t("finalizationSettings.messages.success"), i18n.t("finalizationSettings.messages.reasonUpdated"));
      } else {
        await api.post("/ticketFinalizationReasons", formData);
        toastUtils.success(i18n.t("finalizationSettings.messages.success"), i18n.t("finalizationSettings.messages.reasonCreated"));
      }
      handleCloseModal();
      fetchMotivos();
    } catch (err) {
      toastUtils.error(i18n.t("finalizationSettings.errors.title"), err.response?.data?.error || i18n.t("finalizationSettings.errors.saveReason"));
    }
    setLoading(false);
  }, [formData, editingMotivo, handleCloseModal, fetchMotivos]);

  const handleDelete = useCallback(async (id) => {
    if (window.confirm(i18n.t("finalizationSettings.confirmDelete"))) {
      try {
        await api.delete(`/ticketFinalizationReasons/${id}`);
        toastUtils.success(i18n.t("finalizationSettings.messages.success"), i18n.t("finalizationSettings.messages.reasonDeleted"));
        fetchMotivos();
      } catch (err) {
        toastUtils.error(i18n.t("finalizationSettings.errors.title"), err.response?.data?.error || i18n.t("finalizationSettings.errors.deleteReason"));
      }
    }
  }, [fetchMotivos]);

  const handleInformarValorVendaChange = useCallback(async (event) => {
    const newValue = event.target.checked;
    setInformarValorVenda(newValue);

    try {
      await api.put("/companySettings", {
        column: "informarValorVenda",
        data: newValue,
      });

      if (onSettingsChange) {
        onSettingsChange({ ...settings, informarValorVenda: newValue });
      }

      toastUtils.success(i18n.t("finalizationSettings.messages.success"), i18n.t("finalizationSettings.messages.settingUpdated"));
    } catch (err) {
      toastUtils.error(i18n.t("finalizationSettings.errors.title"), i18n.t("finalizationSettings.errors.updateSetting"));
      setInformarValorVenda(!newValue); // Revert on error
    }
  }, [settings, onSettingsChange]);

  return (
    <Box>
      {/* Header */}
      <Box className={classes.settingContainer}>
        <Box className={classes.sectionHeader}>
          <AssignmentIcon className={classes.sectionIcon} />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {i18n.t("finalizationSettings.title")}
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {i18n.t("finalizationSettings.description")}
        </Typography>

        <Grid container spacing={1}>
          <Grid item>
            <Chip
              icon={informarValorVenda ? <CheckCircleIcon /> : <CancelIcon />}
              label={informarValorVenda ? i18n.t("finalizationSettings.status.salesValueEnabled") : i18n.t("finalizationSettings.status.salesValueDisabled")}
              color={informarValorVenda ? "primary" : "default"}
              variant={informarValorVenda ? "default" : "outlined"}
              className={classes.statusChip}
            />
          </Grid>
          <Grid item>
            <Chip
              icon={<AssignmentIcon />}
              label={`${motivos.length} ${i18n.t("finalizationSettings.status.reasonsRegistered")}`}
              color="default"
              variant="outlined"
              className={classes.statusChip}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Configurações Gerais */}
      <Card style={{ marginBottom: '16px' }}>
        <CardHeader
          avatar={<SettingsIcon />}
          title={i18n.t("finalizationSettings.sections.generalSettings")}
          action={
            <IconButton onClick={() => toggleSection('general')}>
              {expandedSections.general ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.general}>
          <CardContent>
            <FormControlLabel
              control={
                <Checkbox
                  checked={informarValorVenda}
                  onChange={handleInformarValorVendaChange}
                  color="primary"
                />
              }
              label={i18n.t("finalizationSettings.salesValue.label")}
            />
            <Typography variant="body2" color="textSecondary">
              {i18n.t("finalizationSettings.salesValue.description")}
            </Typography>
          </CardContent>
        </Collapse>
      </Card>

      {/* Gestão de Motivos */}
      <Card>
        <CardHeader
          avatar={<AssignmentIcon />}
          title={i18n.t("finalizationSettings.sections.finalizationReasons")}
          action={
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleOpenModal()}
              >
                {i18n.t("finalizationSettings.addReason")}
              </Button>
              <IconButton onClick={() => toggleSection('motivos')}>
                {expandedSections.motivos ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          }
        />
        <Collapse in={expandedSections.motivos}>
          <CardContent>

            <TableContainer>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell>{i18n.t("finalizationSettings.table.name")}</TableCell>
                    <TableCell>{i18n.t("finalizationSettings.table.description")}</TableCell>
                    <TableCell className={classes.actionCell}>{i18n.t("finalizationSettings.table.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {motivos.map((motivo) => (
                    <TableRow key={motivo.id}>
                      <TableCell>
                        <Typography variant="body1">{motivo.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {motivo.description || i18n.t("finalizationSettings.table.noDescription")}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.actionCell}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal(motivo)}
                          color="primary"
                          title={i18n.t("finalizationSettings.actions.edit")}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(motivo.id)}
                          color="secondary"
                          title={i18n.t("finalizationSettings.actions.delete")}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {motivos.length === 0 && (
              <Box textAlign="center" py={3}>
                <Typography variant="body2" color="textSecondary">
                  {i18n.t("finalizationSettings.noReasons")}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Collapse>
      </Card>

      {/* Modal para Adicionar/Editar Motivo */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingMotivo ? i18n.t("finalizationSettings.modal.editTitle") : i18n.t("finalizationSettings.modal.addTitle")}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={i18n.t("finalizationSettings.modal.nameLabel")}
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                variant="outlined"
                margin="dense"
                required
                error={!formData.name.trim() && formData.name !== ""}
                helperText={!formData.name.trim() && formData.name !== "" ? i18n.t("finalizationSettings.modal.nameRequired") : ""}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={i18n.t("finalizationSettings.modal.descriptionLabel")}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                variant="outlined"
                margin="dense"
                multiline
                rows={3}
                placeholder={i18n.t("finalizationSettings.modal.descriptionPlaceholder")}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={loading}>
            {i18n.t("finalizationSettings.modal.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={loading || !formData.name.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading
              ? i18n.t("finalizationSettings.modal.saving")
              : editingMotivo
              ? i18n.t("finalizationSettings.modal.update")
              : i18n.t("finalizationSettings.modal.add")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FinalizacaoAtendimento;
